import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateNotificationSettingsThunk } from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Checkbox from "../../../../components/common/Checkbox";
import {
  BellIcon,
  MailIcon,
  PhoneIcon,
  DeviceMobileIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  SparklesIcon,
} from "@heroicons/react/outline";

const NotificationSettings = ({ admin }) => {
  const dispatch = useDispatch();
  const { loading, notificationSettings: initialSettings } = useSelector(
    (state) => state.admin
  );

  // Local state
  const [settings, setSettings] = useState({
    appointmentReminders: {
      enabled: true,
      advanceMinutes: 30,
    },
    newAppointmentNotification: true,
    cancellationNotification: true,
    reschedulingNotification: true,
    statusChangeNotification: false,
    dailySummary: {
      enabled: true,
      time: "08:00",
    },
    notificationChannels: ["EMAIL", "IN_APP"],
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [testingSMS, setTestingSMS] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // Notification channel options
  const channelOptions = [
    {
      id: "EMAIL",
      label: "Email",
      icon: MailIcon,
      description: "Receive notifications via email",
      color: "blue",
    },
    {
      id: "SMS",
      label: "SMS",
      icon: PhoneIcon,
      description: "Text message notifications",
      color: "green",
    },
    {
      id: "IN_APP",
      label: "In-App",
      icon: BellIcon,
      description: "Notifications within the dashboard",
      color: "purple",
    },
    {
      id: "WHATSAPP",
      label: "WhatsApp",
      icon: DeviceMobileIcon,
      description: "WhatsApp messages",
      color: "emerald",
    },
  ];

  // Reminder timing options
  const reminderTimings = [
    { value: 15, label: "15 minutes before" },
    { value: 30, label: "30 minutes before" },
    { value: 60, label: "1 hour before" },
    { value: 120, label: "2 hours before" },
    { value: 1440, label: "1 day before" },
  ];

  // Initialize settings
  useEffect(() => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings]);

  // Track changes
  useEffect(() => {
    if (initialSettings) {
      const changed =
        JSON.stringify(settings) !== JSON.stringify(initialSettings);
      setHasChanges(changed);
    }
  }, [settings, initialSettings]);

  // Toggle notification channel
  const toggleChannel = (channelId) => {
    setSettings((prev) => {
      const channels = prev.notificationChannels || [];
      return {
        ...prev,
        notificationChannels: channels.includes(channelId)
          ? channels.filter((c) => c !== channelId)
          : [...channels, channelId],
      };
    });
  };

  // Update reminder settings
  const updateReminderSettings = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      appointmentReminders: {
        ...prev.appointmentReminders,
        [field]: value,
      },
    }));
  };

  // Update daily summary
  const updateDailySummary = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      dailySummary: {
        ...prev.dailySummary,
        [field]: value,
      },
    }));
  };

  // Toggle notification type
  const toggleNotificationType = (type) => {
    setSettings((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Handle save
  const handleSave = async () => {
    try {
      await dispatch(updateNotificationSettingsThunk(settings)).unwrap();
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to update notification settings:", error);
    }
  };

  // Handle reset
  const handleReset = () => {
    if (window.confirm("Discard all changes?")) {
      setSettings(initialSettings || {});
      setHasChanges(false);
    }
  };

  // Test notification
  const testNotification = async (channel) => {
    if (channel === "SMS") {
      setTestingSMS(true);
      // Simulate SMS test
      setTimeout(() => {
        setTestingSMS(false);
        alert("Test SMS sent! Check your phone.");
      }, 2000);
    } else if (channel === "EMAIL") {
      setTestingEmail(true);
      // Simulate email test
      setTimeout(() => {
        setTestingEmail(false);
        alert("Test email sent! Check your inbox.");
      }, 2000);
    }
  };

  // Render channel card
  const renderChannelCard = (channel) => {
    const isSelected = (settings.notificationChannels || []).includes(
      channel.id
    );
    const Icon = channel.icon;

    return (
      <div
        key={channel.id}
        className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
          isSelected
            ? `border-${channel.color}-500 bg-${channel.color}-50 ring-2 ring-${channel.color}-500 ring-offset-2`
            : "border-gray-200 hover:border-gray-300 bg-white"
        }`}
        onClick={() => toggleChannel(channel.id)}
      >
        <div className="flex items-start gap-3">
          <div
            className={`p-2 rounded-lg ${
              isSelected ? `bg-${channel.color}-100` : "bg-gray-100"
            }`}
          >
            <Icon
              className={`w-6 h-6 ${
                isSelected ? `text-${channel.color}-600` : "text-gray-600"
              }`}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-semibold text-gray-900">{channel.label}</p>
              {isSelected && (
                <CheckCircleIcon
                  className={`w-5 h-5 text-${channel.color}-600`}
                />
              )}
            </div>
            <p className="text-sm text-gray-600">{channel.description}</p>
            {isSelected && ["EMAIL", "SMS"].includes(channel.id) && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  testNotification(channel.id);
                }}
                disabled={channel.id === "SMS" ? testingSMS : testingEmail}
              >
                {channel.id === "SMS"
                  ? testingSMS
                    ? "Sending..."
                    : "Test SMS"
                  : testingEmail
                  ? "Sending..."
                  : "Test Email"}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
        <div className="flex items-center gap-3">
          <BellIcon className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Notification Preferences
            </h2>
            <p className="text-sm text-gray-600">
              Customize how and when you receive appointment notifications
            </p>
          </div>
        </div>
      </div>

      {/* Notification Channels */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <SparklesIcon className="w-4 h-4 text-primary" />
          Notification Channels
          <Badge variant="info">
            {(settings.notificationChannels || []).length} selected
          </Badge>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {channelOptions.map((channel) => renderChannelCard(channel))}
        </div>
      </div>

      {/* Appointment Reminders */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <BellIcon className="w-4 h-4 text-primary" />
          Appointment Reminders
        </h3>
        <div className="bg-gray-50 rounded-lg p-4 border space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={settings.appointmentReminders?.enabled}
              onChange={(e) =>
                updateReminderSettings("enabled", e.target.checked)
              }
            />
            <div>
              <p className="font-medium text-sm text-gray-900">
                Enable Appointment Reminders
              </p>
              <p className="text-xs text-gray-600">
                Get notified before appointments
              </p>
            </div>
          </label>

          {settings.appointmentReminders?.enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Timing
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {reminderTimings.map((timing) => (
                  <button
                    key={timing.value}
                    onClick={() =>
                      updateReminderSettings("advanceMinutes", timing.value)
                    }
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      settings.appointmentReminders?.advanceMinutes ===
                      timing.value
                        ? "border-primary bg-blue-50 text-primary"
                        : "border-gray-200 hover:border-gray-300 bg-white text-gray-700"
                    }`}
                  >
                    {timing.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notification Types */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Notification Types
        </h3>
        <div className="bg-gray-50 rounded-lg p-4 border space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={settings.newAppointmentNotification}
              onChange={() =>
                toggleNotificationType("newAppointmentNotification")
              }
            />
            <div>
              <p className="font-medium text-sm text-gray-900">
                New Appointments
              </p>
              <p className="text-xs text-gray-600">
                When a customer books an appointment with you
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={settings.cancellationNotification}
              onChange={() =>
                toggleNotificationType("cancellationNotification")
              }
            />
            <div>
              <p className="font-medium text-sm text-gray-900">Cancellations</p>
              <p className="text-xs text-gray-600">
                When an appointment is cancelled
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={settings.reschedulingNotification}
              onChange={() =>
                toggleNotificationType("reschedulingNotification")
              }
            />
            <div>
              <p className="font-medium text-sm text-gray-900">Rescheduling</p>
              <p className="text-xs text-gray-600">
                When an appointment is rescheduled
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={settings.statusChangeNotification}
              onChange={() =>
                toggleNotificationType("statusChangeNotification")
              }
            />
            <div>
              <p className="font-medium text-sm text-gray-900">
                Status Changes
              </p>
              <p className="text-xs text-gray-600">
                When your availability status changes automatically
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Daily Summary */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Daily Summary
        </h3>
        <div className="bg-gray-50 rounded-lg p-4 border space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={settings.dailySummary?.enabled}
              onChange={(e) => updateDailySummary("enabled", e.target.checked)}
            />
            <div>
              <p className="font-medium text-sm text-gray-900">
                Enable Daily Summary
              </p>
              <p className="text-xs text-gray-600">
                Get a summary of your daily appointments
              </p>
            </div>
          </label>

          {settings.dailySummary?.enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Summary Time
              </label>
              <input
                type="time"
                value={settings.dailySummary?.time || "08:00"}
                onChange={(e) => updateDailySummary("time", e.target.value)}
                className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-gray-600 mt-1">
                You'll receive a summary at this time each day
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Presets */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Quick Presets
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() =>
              setSettings({
                appointmentReminders: { enabled: true, advanceMinutes: 30 },
                newAppointmentNotification: true,
                cancellationNotification: true,
                reschedulingNotification: true,
                statusChangeNotification: false,
                dailySummary: { enabled: false },
                notificationChannels: ["EMAIL", "IN_APP"],
              })
            }
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-blue-50 transition-all text-left"
          >
            <p className="font-medium text-gray-900 mb-1">📧 Email Only</p>
            <p className="text-xs text-gray-600">
              Email + in-app, essential notifications
            </p>
          </button>

          <button
            onClick={() =>
              setSettings({
                appointmentReminders: { enabled: true, advanceMinutes: 60 },
                newAppointmentNotification: true,
                cancellationNotification: true,
                reschedulingNotification: true,
                statusChangeNotification: true,
                dailySummary: { enabled: true, time: "08:00" },
                notificationChannels: ["EMAIL", "SMS", "IN_APP"],
              })
            }
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-blue-50 transition-all text-left"
          >
            <p className="font-medium text-gray-900 mb-1">
              🔔 All Notifications
            </p>
            <p className="text-xs text-gray-600">
              All channels, all types, daily summary
            </p>
          </button>

          <button
            onClick={() =>
              setSettings({
                appointmentReminders: { enabled: true, advanceMinutes: 15 },
                newAppointmentNotification: true,
                cancellationNotification: false,
                reschedulingNotification: false,
                statusChangeNotification: false,
                dailySummary: { enabled: false },
                notificationChannels: ["IN_APP"],
              })
            }
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-blue-50 transition-all text-left"
          >
            <p className="font-medium text-gray-900 mb-1">🔕 Minimal</p>
            <p className="text-xs text-gray-600">
              In-app only, appointment reminders
            </p>
          </button>
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-white border-t pt-4 flex justify-between items-center">
          <p className="text-sm text-orange-600 font-medium flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4" />
            You have unsaved changes
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Notification Tips</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Enable multiple channels for important notifications</li>
              <li>Test your notification channels to ensure they work</li>
              <li>
                Set reminder timing based on typical travel time to your
                location
              </li>
              <li>Daily summaries help you prepare for the day ahead</li>
              <li>
                You can customize settings anytime based on your preferences
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
